import { apiClient } from '../client';
import type { Bar } from './bars';

interface UpdateBarData {
  name?: string;
  description?: string;
  logoUrl?: string;
  hours?: Record<string, { open: string; close: string }>;
  address?: string;
  websiteUrl?: string;
  phoneNumber?: string;
  timeZone?: string;
  servesBrunch?: boolean;
  outdoorSeating?: boolean;
  allowsDogs?: boolean;
  goodForWatchingSports?: boolean;
  servesBeer?: boolean;
  servesWine?: boolean;
  servesCocktails?: boolean;
  // Content mode fields
  contentMode?: 'auto' | 'manual' | 'hybrid';
  manualFields?: string[];
  aiGuidance?: string;
  specialsMode?: 'auto' | 'manual';
  eventsMode?: 'auto' | 'manual';
}

interface ExtractParams {
  barId?: string;
  staleDays?: number;
  limit?: number;
  async?: boolean;
  force?: boolean;
}

interface CrawlParams {
  barId?: string;
  limit?: number;
  async?: boolean;
}

export interface ClaimRequest {
  id: string;
  barId: string;
  userId: string;
  status: 'pending' | 'approved' | 'rejected';
  autoApproved: boolean;
  createdAt: string;
  barName: string;
  barWebsite: string | null;
  barAddress: string;
  userEmail: string;
  userName: string | null;
}

interface ClaimRequestsResponse {
  success: boolean;
  requests: ClaimRequest[];
  count: number;
}

export const adminApi = {
  updateBar: async (id: string, data: UpdateBarData): Promise<{ success: boolean; bar: Bar }> => {
    const response = await apiClient.patch(`/admin/bars/${id}`, data);
    return response.data;
  },

  triggerExtraction: async (params: ExtractParams = {}) => {
    const response = await apiClient.post('/admin/extract', params);
    return response.data;
  },

  triggerCrawl: async (params: CrawlParams = {}) => {
    const response = await apiClient.post('/admin/crawl', params);
    return response.data;
  },

  getBarsNeedingCrawling: async (limit = 50) => {
    const response = await apiClient.get('/admin/bars/needing-crawling', {
      params: { limit },
    });
    return response.data;
  },

  getBarsNeedingExtraction: async (staleDays = 7, limit = 50) => {
    const response = await apiClient.get('/admin/bars/needing-extraction', {
      params: { staleDays, limit },
    });
    return response.data;
  },

  getClaimRequests: async (): Promise<ClaimRequestsResponse> => {
    const response = await apiClient.get<ClaimRequestsResponse>('/admin/claim-requests');
    return response.data;
  },

  approveClaimRequest: async (id: string) => {
    const response = await apiClient.post(`/admin/claim-requests/${id}/approve`);
    return response.data;
  },

  rejectClaimRequest: async (id: string) => {
    const response = await apiClient.post(`/admin/claim-requests/${id}/reject`);
    return response.data;
  },

  // Job Queue
  getJobQueueStatus: async () => {
    const response = await apiClient.get('/admin/jobs');
    return response.data;
  },

  // Script Execution
  runGenerateSlugs: async () => {
    const response = await apiClient.post('/admin/scripts/generate-slugs');
    return response.data;
  },

  runDiscoverLocations: async () => {
    const response = await apiClient.post('/admin/scripts/discover-locations');
    return response.data;
  },

  // Content Metrics
  getContentMetrics: async (sinceDays = 7) => {
    const response = await apiClient.get('/admin/content-metrics', {
      params: { sinceDays },
    });
    return response.data;
  },

  // Discovery Log
  getDiscoveryLog: async (params: { metroId?: string; sinceDays?: number; reason?: string; search?: string; limit?: number; offset?: number } = {}) => {
    const response = await apiClient.get('/admin/discovery-log', { params });
    return response.data;
  },

  getDiscoveryLogDaily: async (params: { days?: number; metroId?: string } = {}) => {
    const response = await apiClient.get('/admin/discovery-log/daily', { params });
    return response.data;
  },

  cleanupDiscoveryLog: async (daysOld = 90) => {
    const response = await apiClient.post('/admin/discovery-log/cleanup', { daysOld });
    return response.data;
  },

  // Logo upload
  uploadBarLogo: async (barId: string, file: File): Promise<{ success: boolean; bar: Bar; logoUrl: string }> => {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await apiClient.post(`/admin/bars/${barId}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Analytics Dashboard
  getGlobalAnalytics: async (days = 7) => {
    const response = await apiClient.get('/admin/analytics/global', { params: { days } });
    return response.data;
  },

  getAnalyticsMap: async (days = 7) => {
    const response = await apiClient.get('/admin/analytics/map', { params: { days } });
    return response.data;
  },
};
